/**
 * Profile Dialog Component
 * 
 * Modal dialog for connecting and editing Upwork profiles.
 * Per design.md: Premium UI, semantic tokens, lucide icons.
 * Per workspace rules: Friendly error messages, no server details.
 */

import { useState, useEffect } from 'react';
import { useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
    Button,
    Input,
    Textarea,
    Badge,
    Loader2,
    X,
} from '@uprosal/ui';

interface ProfileDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    editProfile?: Doc<'connectedProfiles'> | null;
    onSuccess?: () => void;
}

// Friendly error messages for users
const FRIENDLY_ERRORS: Record<string, string> = {
    'Invalid Upwork URL': 'Please enter a valid Upwork profile URL.',
    'Profile label is required': 'Please enter a name for your profile.',
};

function getFriendlyError(error: unknown): string {
    if (error instanceof Error) {
        for (const [pattern, friendly] of Object.entries(FRIENDLY_ERRORS)) {
            if (error.message.includes(pattern)) {
                return friendly;
            }
        }
        return 'Something went wrong. Please try again.';
    }
    return 'Something went wrong. Please try again.';
}

export function ProfileDialog({ open, onOpenChange, editProfile, onSuccess }: ProfileDialogProps) {
    const [profileLabel, setProfileLabel] = useState('');
    const [upworkUrl, setUpworkUrl] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [skillInput, setSkillInput] = useState('');
    const [skills, setSkills] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const upsertProfile = useMutation(api.mutations.profiles.upsert);
    const updateProfile = useMutation(api.mutations.profiles.updateManual);

    const isEditing = !!editProfile;

    // Reset form when dialog opens/closes or profile changes
    useEffect(() => {
        if (open && editProfile) {
            setProfileLabel(editProfile.profileLabel);
            setUpworkUrl(editProfile.upworkProfileUrl);
            setTitle(editProfile.title ?? '');
            setDescription(editProfile.description ?? '');
            setSkills(editProfile.skills);
        } else if (open && !editProfile) {
            setProfileLabel('');
            setUpworkUrl('');
            setTitle('');
            setDescription('');
            setSkills([]);
        }
        setSkillInput('');
        setError(null);
    }, [open, editProfile]);

    const handleAddSkill = () => {
        const trimmed = skillInput.trim();
        if (trimmed && !skills.includes(trimmed)) {
            setSkills([...skills, trimmed]);
            setSkillInput('');
        }
    };

    const handleRemoveSkill = (skill: string) => {
        setSkills(skills.filter((s) => s !== skill));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddSkill();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const trimmedLabel = profileLabel.trim();
        const trimmedUrl = upworkUrl.trim();

        // Validation
        if (!trimmedLabel) {
            setError('Please enter a profile name (e.g., "Main", "Backend Dev").');
            return;
        }
        if (trimmedLabel.length > 50) {
            setError('Profile name is too long. Maximum 50 characters.');
            return;
        }
        if (!trimmedUrl) {
            setError('Please enter your Upwork profile URL.');
            return;
        }
        // Basic URL validation
        if (!trimmedUrl.includes('upwork.com')) {
            setError('Please enter a valid Upwork profile URL.');
            return;
        }

        setIsSaving(true);

        try {
            if (isEditing && editProfile) {
                await updateProfile({
                    profileId: editProfile._id,
                    patch: {
                        profileLabel: trimmedLabel,
                        title: title.trim() || undefined,
                        description: description.trim() || undefined,
                        skills: skills,
                    },
                });
            } else {
                await upsertProfile({
                    profileLabel: trimmedLabel,
                    upworkProfileUrl: trimmedUrl,
                    title: title.trim() || undefined,
                    description: description.trim() || undefined,
                    skills: skills,
                });
            }

            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            console.error('Failed to save profile:', err);
            setError(getFriendlyError(err));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[520px] max-w-[90vw] max-h-[85vh] overflow-y-auto">
                <DialogClose onClick={() => onOpenChange(false)} />

                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Edit Profile' : 'Connect Upwork Profile'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? 'Update your profile details.'
                            : 'Add your Upwork profile for personalized AI analysis and proposals.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {/* Profile Label */}
                        <div className="space-y-2">
                            <label
                                htmlFor="profile-label"
                                className="text-sm font-medium text-foreground"
                            >
                                Profile Name <span className="text-destructive">*</span>
                            </label>
                            <Input
                                id="profile-label"
                                placeholder="e.g., Main, Backend Dev, Mobile Expert"
                                value={profileLabel}
                                onChange={(e) => setProfileLabel(e.target.value)}
                                disabled={isSaving}
                                maxLength={50}
                            />
                            <p className="text-xs text-muted-foreground">
                                A friendly name to identify this profile
                            </p>
                        </div>

                        {/* Upwork URL */}
                        <div className="space-y-2">
                            <label
                                htmlFor="upwork-url"
                                className="text-sm font-medium text-foreground"
                            >
                                Upwork Profile URL <span className="text-destructive">*</span>
                            </label>
                            <Input
                                id="upwork-url"
                                placeholder="https://www.upwork.com/freelancers/~..."
                                value={upworkUrl}
                                onChange={(e) => setUpworkUrl(e.target.value)}
                                disabled={isSaving || isEditing}
                                type="url"
                            />
                            {isEditing && (
                                <p className="text-xs text-muted-foreground">
                                    URL cannot be changed after creation
                                </p>
                            )}
                        </div>

                        {/* Title */}
                        <div className="space-y-2">
                            <label
                                htmlFor="profile-title"
                                className="text-sm font-medium text-foreground"
                            >
                                Professional Title
                            </label>
                            <Input
                                id="profile-title"
                                placeholder="e.g., Full Stack Developer, UI/UX Designer"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={isSaving}
                                maxLength={200}
                            />
                        </div>

                        {/* Skills */}
                        <div className="space-y-2">
                            <label
                                htmlFor="profile-skills"
                                className="text-sm font-medium text-foreground"
                            >
                                Skills
                            </label>
                            <div className="flex gap-2">
                                <Input
                                    id="profile-skills"
                                    placeholder="Type a skill and press Enter"
                                    value={skillInput}
                                    onChange={(e) => setSkillInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={isSaving}
                                />
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleAddSkill}
                                    disabled={isSaving || !skillInput.trim()}
                                >
                                    Add
                                </Button>
                            </div>
                            {skills.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {skills.map((skill) => (
                                        <Badge key={skill} variant="secondary" className="gap-1 pr-1">
                                            {skill}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSkill(skill)}
                                                className="ml-1 hover:bg-muted rounded-full p-0.5"
                                                disabled={isSaving}
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <label
                                htmlFor="profile-description"
                                className="text-sm font-medium text-foreground"
                            >
                                Profile Summary
                            </label>
                            <Textarea
                                id="profile-description"
                                placeholder="Paste your Upwork profile overview here (optional but recommended for better AI analysis)"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={isSaving}
                                className="min-h-[100px]"
                            />
                            <p className="text-xs text-muted-foreground">
                                {description.length}/5,000 characters
                            </p>
                        </div>

                        {/* Error */}
                        {error && (
                            <p className="text-sm text-destructive">{error}</p>
                        )}
                    </div>

                    <DialogFooter className="mt-6">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : isEditing ? (
                                'Save Changes'
                            ) : (
                                'Connect Profile'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
